/// <reference types="vite/client" />
import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';

import { Link } from '@/core/i18n/navigation';
import { envConfigs } from '@/config';
import { getQueryClient } from '@/lib/query-client';
import { seoHead } from '@/lib/seo';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { Footer } from '@/blocks/footer';
import { Header } from '@/blocks/header';
import { Clarity } from '@/components/analytics/clarity';
import { GoogleAnalytics } from '@/components/analytics/google-analytics';
import { Plausible } from '@/components/analytics/plausible';
import { SandboxPreviewBridge } from '@/components/sandbox-preview-bridge';
import { Toaster } from '@/components/ui/sonner';
import manifest from '@/content/control-resonant/page-manifest.json';

import '@/styles/globals.css';

export const Route = createRootRoute({
  head: ({ matches }) => {
    const missing = matches.some(match => match.status === 'notFound' || match.globalNotFound ||
      (/^\/(puzzles|abilities|quests|guides|trophies)\/\$slug$/.test(match.routeId) && !manifest.some(page => page.url === match.pathname)));
    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'theme-color', content: '#0B0D10' },
        ...seoHead({
          title: missing
            ? `${m['control.not_found.title']()} | ${envConfigs.app_name}`
            : envConfigs.app_name,
          description: missing
            ? m['control.not_found.body']()
            : envConfigs.app_description,
          url: '/',
          indexable: false,
        }).meta,
      ],
      links: [
        { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
        {
          rel: 'icon',
          href: '/favicon-32x32.png',
          type: 'image/png',
          sizes: '32x32',
        },
        { rel: 'manifest', href: '/site.webmanifest' },
        { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      ],
    };
  },
  component: RootComponent,
  shellComponent: RootDocument,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootComponent() {
  const analytics = {
    gaId: envConfigs.public_google_analytics_id,
    plausibleDomain: envConfigs.public_plausible_domain,
    plausibleSrc: envConfigs.public_plausible_src,
  };

  return (
    <QueryClientProvider client={getQueryClient()}>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem={false}
        disableTransitionOnChange
      >
        <Outlet />
        <SandboxPreviewBridge />
        <Toaster position="top-center" richColors />

        {analytics?.gaId ? (
          <GoogleAnalytics measurementId={analytics.gaId} />
        ) : null}
        {envConfigs.public_clarity_id && (
          <Clarity projectId={envConfigs.public_clarity_id} />
        )}
        {analytics?.plausibleDomain || analytics?.plausibleSrc ? (
          <Plausible
            domain={analytics.plausibleDomain}
            src={analytics.plausibleSrc || undefined}
          />
        ) : null}
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang={getLocale() === 'zh' ? 'zh-CN' : 'en'} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased">
        <a href="#main-content" className="crt-skip">
          {m['control.skip']()}
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function NotFound() {
  return (
    <>
      <Header />
      <main id="main-content" className="crt-container crt-guide">
        <div className="crt-lost-signal" aria-hidden="true">
          <img src="/logo-mark.svg" width="96" height="96" alt="" />
        </div>
        <h1>{m['control.not_found.title']()}</h1>
        <p className="crt-lead">{m['control.not_found.body']()}</p>
        <nav aria-label={m['control.recovery']()} className="crt-actions">
          {[
            ['/', m['control.home']()],
            ['/build-planner', m['control.nav.planner']()],
            ['/puzzles', m['control.nav.puzzles']()],
            ['/abilities', m['control.nav.abilities']()],
            ['/quests', m['control.nav.quests']()],
          ].map(([href, label]) => (
            <Link
              className="crt-button crt-button-secondary"
              key={href}
              href={href}
            >
              {label}
            </Link>
          ))}
        </nav>
      </main>
      <Footer />
    </>
  );
}

function RootError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center gap-4">
      <h1 className="text-4xl font-bold">Oops</h1>
      <p className="text-muted-foreground">
        Something went wrong. Please try again.
      </p>
      {import.meta.env.DEV && error instanceof Error && (
        <pre className="bg-muted mt-2 max-w-lg overflow-auto rounded p-4 text-xs">
          {error.message}
        </pre>
      )}
      <button
        type="button"
        onClick={reset}
        className="text-sm underline underline-offset-4"
      >
        Try again
      </button>
    </div>
  );
}
